import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2, Play, ShieldCheck, Zap, Rocket, Star, Info, Monitor, Smartphone, LayoutGrid, ListChecks, Download } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/tutoriais-iptv/tv-box")({
  head: () => ({
    meta: [
      { title: "Como Instalar IPTV no TV Box: Guia Completo Play Store" },
      { name: "description", content: "Aprenda como configurar IPTV no seu TV Box via Play Store. Guia completo com apps OneX Player, RP725 e VUser para Painel IPTV Pós Pago." },
    ],
  }),
  component: TVBoxTutorial,
});

function TVBoxTutorial() {
  const mainApps = [
    { 
      name: "OneX Player", 
      type: "Aplicativo Próprio",
      desc: "Nossa solução exclusiva integrada ao sistema. Oferece a melhor experiência, rapidez e interface moderna.",
      features: ["Interface Premium", "Atualizações Automáticas", "Alta Performance", "Otimizado para Controle"]
    },
    { 
      name: "RP725", 
      type: "Aplicativo Parceiro",
      desc: "Excelente estabilidade e navegação intuitiva. Um dos apps mais baixados para dispositivos Android.",
      features: ["Navegação Simples", "Instalação Rápida", "Leve", "Compatível com Google TV"]
    },
    { 
      name: "VUser", 
      type: "Aplicativo Parceiro",
      desc: "Configuração simples e interface amigável. Ótimo desempenho em aparelhos com hardware básico.",
      features: ["Fácil Configuração", "Layout Limpo", "Estabilidade", "Suporte Android TV"]
    }
  ];

  const tutorialSteps = [
    { title: "Conexão", description: "Conecte seu TV Box à internet via Wi-Fi ou Cabo." },
    { title: "Google Play", description: "Abra a loja oficial Google Play Store no seu aparelho." },
    { title: "Busca", description: "Pesquise por 'OneX Player', 'RP725' ou 'VUser'." },
    { title: "Instalação", description: "Clique em instalar e aguarde o download concluir." },
    { title: "Acesso", description: "Abra o app e insira os dados fornecidos pelo seu revendedor." }
  ];

  return (
    <SEOPage
      title="Como Instalar IPTV no TV Box: Guia Completo Play Store"
      subtitle="Transforme seu TV Box em uma central de entretenimento definitiva com os apps oficiais da Play Store."
      category="Tutoriais TV Box"
      introParagraphs={[
        "O TV Box é, sem dúvida, o dispositivo mais versátil para quem busca a melhor experiência IPTV. Por rodar o sistema Android, ele permite o acesso à vasta biblioteca da Google Play Store, garantindo segurança e facilidade na instalação dos aplicativos.",
        "Trabalhamos com uma estrutura de Painel IPTV Pós Pago otimizada especificamente para o ecossistema Android, proporcionando a revendedores e clientes finais uma estabilidade de sinal incomparável em 2026."
      ]}
      breadcrumbs={[
        { label: "Início", href: "/" },
        { label: "Tutoriais", href: "/tutoriais-iptv" },
        { label: "TV Box", href: "/tutoriais-iptv/tv-box" }
      ]}
      benefits={[
        { title: "Android Nativo", description: "Compatibilidade total com o ecossistema Google e Play Store." },
        { title: "Instalação Oficial", description: "Apps seguros baixados diretamente da loja oficial Android." },
        { title: "Alta Estabilidade", description: "Servidores otimizados para hardware de TV Box e Android TV." },
        { title: "Multimídia", description: "Suporte para 4K, HDR e som surround nos apps compatíveis." },
        { title: "Gestão Ágil", description: "Painel Pós-Pago com ativação imediata para seus clientes." },
        { title: "Versatilidade", description: "Funciona em TV Box, Mi Stick, Fire Stick e Android TV." }
      ]}
      howItWorks={tutorialSteps}
      diferenciais={[
        { title: "Sem Investimento Prévio", description: "O modelo pós-pago permite que você cresça sem comprar créditos antecipados." },
        { title: "Apps Próprios", description: "Desenvolvemos players exclusivos para garantir que seu cliente não tenha quedas." },
        { title: "Marca Brópria", description: "Personalize sua operação e fortaleça sua marca no mercado IPTV." },
        { title: "Suporte Técnico", description: "Equipe especializada para auxiliar em qualquer configuração de hardware." }
      ]}
      faq={[
        { question: "Preciso baixar APK via navegador?", answer: "Não é necessário. Nossos principais aplicativos (OneX, RP725, VUser) estão disponíveis oficialmente na Play Store." },
        { question: "Funciona em aparelhos antigos?", answer: "Sim, nossos aplicativos são leves e otimizados para rodar em TV Box com versões de Android a partir da 7.0." },
        { question: "Qual o app recomendado para TV Box?", answer: "Recomendamos fortemente o OneX Player por ser integrado à nossa infraestrutura premium." },
        { question: "O Painel Pós Pago é para revendedores?", answer: "Sim, é a ferramenta definitiva para quem quer escalar as vendas sem riscos financeiros iniciais." },
        { question: "Preciso de um login para testar?", answer: "Sim. Fale conosco agora mesmo para receber um teste gratuito e comprovar a qualidade." }
      ]}
      sections={[
        {
          title: "Aplicativos em Destaque (Play Store)",
          type: "grid",
          content: (
            <>
              {mainApps.map((app, i) => (
                <div key={i} className="p-8 rounded-[2rem] bg-white/[0.02] border border-white/5 flex flex-col h-full hover:border-electric/30 transition-all group">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-electric mb-2 block">{app.type}</span>
                      <h3 className="text-2xl font-bold">{app.name}</h3>
                    </div>
                    <div className="bg-electric/10 p-3 rounded-2xl group-hover:bg-electric group-hover:text-white transition-all">
                      <Download className="w-5 h-5" />
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-8 flex-grow">{app.desc}</p>
                  <ul className="space-y-3">
                    {app.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60">
                        <CheckCircle2 className="w-4 h-4 text-electric" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          )
        },
        {
          title: "Tudo sobre o Painel Pós Pago",
          type: "featured",
          content: (
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <h3 className="text-3xl font-black italic uppercase tracking-tighter">
                  A Revolução da <span className="text-electric">Revenda IPTV</span>
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Diferente do sistema pré-pago onde você 'congela' seu dinheiro em créditos, no sistema Pós-Pago P2PRO você tem liberdade total para expandir.
                </p>
                <div className="grid gap-4">
                  <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10">
                    <Zap className="w-6 h-6 text-electric" />
                    <span className="font-bold text-sm">Ativação Instantânea via Dashboard</span>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10">
                    <ShieldCheck className="w-6 h-6 text-electric" />
                    <span className="font-bold text-sm">Garantia de Uptime de 99.9%</span>
                  </div>
                </div>
              </div>
              <div className="relative aspect-video rounded-3xl overflow-hidden border border-white/10 shadow-glow bg-background flex flex-col items-center justify-center p-8 text-center">
                <LayoutGrid className="w-16 h-16 text-electric/20 mb-6" />
                <h4 className="text-xl font-black uppercase italic mb-2">Interface Profissional</h4>
                <p className="text-xs text-muted-foreground italic">Gerencie centenas de clientes com apenas alguns cliques no seu TV Box ou Smartphone.</p>
              </div>
            </div>
          )
        }
      ]}
      ctaTitle="🚀 Comece Sua Revenda no TV Box Agora"
      ctaDescription="Tenha acesso aos melhores aplicativos da Play Store e à maior infraestrutura Pós-Paga do Brasil."
    />
  );
}
